import {
  ComponentPropsWithoutRef,
  ReactElement,
  UIEvent,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router";
import { getScrollRestorationStateKey } from "../../utils/scrollRestoration";
import { useScrollPositionRestoration } from "./useScrollPositionRestoration";

type RouteScrollAreaProps = ComponentPropsWithoutRef<"div"> & {
  restorationEnabled?: boolean;
  restorationId?: string;
};

export const RouteScrollArea = ({
  children,
  onScroll,
  restorationEnabled = true,
  restorationId = "default",
  ...props
}: RouteScrollAreaProps): ReactElement => {
  const location = useLocation();
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const routeEntryKey =
    getScrollRestorationStateKey(location.state) ?? location.key;
  const getScrollPosition = useCallback(
    () => scrollElementRef.current?.scrollTop ?? 0,
    [],
  );
  const restoreScrollPosition = useCallback((scrollTop: number) => {
    scrollElementRef.current?.scrollTo({ top: scrollTop });
  }, []);
  const saveScrollPosition = useScrollPositionRestoration({
    enabled: restorationEnabled,
    restorationKey: `route:${routeEntryKey}:${restorationId}`,
    getScrollPosition,
    restoreScrollPosition,
  });
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      saveScrollPosition();
      onScroll?.(event);
    },
    [onScroll, saveScrollPosition],
  );

  return (
    <div ref={scrollElementRef} onScroll={handleScroll} {...props}>
      {children}
    </div>
  );
};
